/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * TdReminder — shows the next reminder date + type in a sidebar card.
 * Pulls from `fetchReminderData()` (#163) on connect; formats the date
 * via `Intl.DateTimeFormat` honouring the user's browser locale.
 *
 * Lit light-DOM component (#206). The `reminderDate` / `type` attribute
 * round-trip from the vanilla version was dropped — `attributeChangedCallback`
 * never fired for the camelCase `reminderDate` attribute in HTML, so the
 * attribute-as-public-API was a no-op. The data now lives on `@state`
 * fields driven by the fetch.
 */

import { LitElement, html, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { fetchReminderData, type ReminderData } from '../services/dashboard.js';
import { t, LocaleController } from '../services/i18n.js';

@customElement('td-reminder')
export class TdReminder extends LitElement {
  @state() private reminderData: ReminderData | undefined = undefined;

  /** Re-renders this component when the UI locale changes (#89). */
  readonly localeController = new LocaleController(this);

  override createRenderRoot(): HTMLElement {
    return this;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    void this.loadReminderData();
  }

  async loadReminderData(): Promise<void> {
    try {
      this.reminderData = await fetchReminderData();
    } catch (error) {
      console.error('Failed to load reminder data:', error);
    }
  }

  formatDate(dateString: string): string {
    if (!dateString) return t('reminder.noDate');

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      return new Intl.DateTimeFormat(navigator.language, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(date);
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  }

  override render(): TemplateResult {
    if (!this.reminderData) {
      return html`
        <wa-card>
          <h3>${t('reminder.title')}</h3>
          <p>${t('reminder.loading')}</p>
        </wa-card>
      `;
    }
    return html`
      <wa-card>
        <h3>${t('reminder.title')}</h3>
        <div class="wa-stack wa-gap-3xs">
          <span class="wa-heading-s reminder-date">
            ${this.formatDate(this.reminderData.date)}
          </span>
          <span class="wa-caption-s" lang="la">
            <em>${this.reminderData.type || t('reminder.na')}</em>
          </span>
        </div>
      </wa-card>
    `;
  }
}
